import { Cell, Grid, HFlow, Icon, Theme, useStyles } from 'bold-ui'
import { Form, FormDebouncedValueSpy, SwitchField, TextField } from 'components/form'
import { PopperButton, PopperControls } from 'components/popper'
import { FormState } from 'final-form'
import { isEqual } from 'lodash'
import moment from 'moment'
import React, { CSSProperties, useEffect, useState } from 'react'

import { ListaAtendimentoDefaultFilter } from '../ListaAtendimentoView'
import { ListaAtendimentoFilterPopperForm, ListaAtendimentoFilterPopperModel } from './ListaAtendimentoFilterPopperForm'
import { ListaAtendimentoFilterTags } from './ListaAtendimentoFilterTags'
import { ListaAtendimentoSortDropdown } from './ListaAtendimentoSortDropdown'

export interface ListaAtendimentoFilterProps {
  filter: ListaAtendimentoFilterModel
  onChangeFilter(values: ListaAtendimentoFilterModel): void
  filterBackup: ListaAtendimentoFilterPopperModel
  updateFilterBackup(values: ListaAtendimentoFilterPopperModel): void
  deleteFilterBackup(): void
}

export type ListaAtendimentoFilterModel = ListaAtendimentoInlineFilterModel & ListaAtendimentoFilterPopperModel

export interface ListaAtendimentoInlineFilterModel {
  query?: string
  somenteMeusAtendimentos?: boolean
  registroTardio?: boolean
}

export function ListaAtendimentoFilter(props: ListaAtendimentoFilterProps) {
  const { filter, filterBackup, onChangeFilter, updateFilterBackup, deleteFilterBackup } = props
  const { query, somenteMeusAtendimentos, ...restFilter } = filter
  const [countFiltrosAplicados, setCountFiltrosAplicados] = useState(0)
  const [isFilterEqual, setIsFilterEqual] = useState(false)

  const { classes } = useStyles(createStyles)

  const mountListaAtendimentoFilterPopperFormInitialValues = (values: ListaAtendimentoFilterPopperModel) => ({
    ...values,
    periodo: {
      startDate: values.somenteNaoFinalizados
        ? undefined
        : values.periodo?.startDate && moment(values.periodo.startDate).toDate(),
      endDate: values.somenteNaoFinalizados
        ? undefined
        : values.periodo?.endDate && moment(values.periodo.endDate).toDate(),
    },
  })

  const initialValues = mountListaAtendimentoFilterPopperFormInitialValues(filterBackup || restFilter)

  const handleDebounceChange = (formState: FormState<ListaAtendimentoInlineFilterModel>) => {
    onChangeFilter({ ...filter, ...formState.values, query: formState.values.query })
  }

  const handlePopperFormSubmit = (values: ListaAtendimentoFilterPopperModel) => {
    deleteFilterBackup()
    onChangeFilter({ ...filter, ...values })
  }

  const handleClear = () => {
    deleteFilterBackup()
    onChangeFilter(ListaAtendimentoDefaultFilter.filter)
  }

  const handleClose = () => {
    deleteFilterBackup()
  }

  const renderFilterForm = () => {
    return (
      <>
        <FormDebouncedValueSpy onChange={handleDebounceChange} loadOnMount={false} />
        <Grid alignItems='center'>
          <Cell size={5} xl={4} style={classes.cell1}>
            <TextField
              name='query'
              placeholder='Pesquise por nome, CPF, CNS ou data de nascimento'
              icon='zoomOutline'
            />
          </Cell>
          <Cell size={7} xl={8} style={classes.cell2}>
            <HFlow style={classes.hFlow}>
              <SwitchField
                style={{
                  display: 'grid',
                  gridAutoFlow: 'column',
                  whiteSpace: 'nowrap',
                  justifyContent: 'start',
                  maxWidth: '16.5rem',
                }}
                name='somenteMeusAtendimentos'
                label='Ver somente os meus atendimentos'
              />
              <ListaAtendimentoSortDropdown filter={filter} onChange={onChangeFilter} />
            </HFlow>
          </Cell>
        </Grid>
      </>
    )
  }

  const renderPopper = (ctrl: PopperControls) => (
    <ListaAtendimentoFilterPopperForm
      initialValues={initialValues}
      onSubmit={handlePopperFormSubmit}
      onFechar={ctrl.close}
      onClear={handleClear}
      onChangeFilter={updateFilterBackup}
      isFilterEqual={isFilterEqual}
    />
  )

  useEffect(() => {
    setCountFiltrosAplicados(
      (filter?.statusAtendimento?.length || 0) +
        (filter?.tiposServico?.length || 0) +
        (filter?.equipes?.length || 0) +
        (filter?.lotacoes?.length || 0) +
        (filter?.somenteNaoFinalizados ? 1 : 0) +
        (filter?.periodo?.endDate && filter?.periodo?.startDate ? 1 : 0)
    )

    const start =
      filter.periodo?.startDate &&
      moment(filter.periodo.startDate).isSame(ListaAtendimentoDefaultFilter.filter.periodo.startDate, 'day')
    const end =
      filter.periodo?.endDate &&
      moment(filter.periodo.endDate).isSame(ListaAtendimentoDefaultFilter.filter.periodo.endDate, 'day')

    const rest = isEqual(
      { ...filter, periodo: undefined, query: filter.query || undefined },
      { ...ListaAtendimentoDefaultFilter.filter, periodo: undefined, query: undefined }
    )

    setIsFilterEqual(start && end && rest)
  }, [filter])

  return (
    <Grid gap={0.5}>
      <Cell size={12}>
        <HFlow style={classes.hFlow}>
          <Form<ListaAtendimentoInlineFilterModel>
            onSubmit={onChangeFilter}
            render={renderFilterForm}
            initialValues={{ query, somenteMeusAtendimentos }}
          />
          <PopperButton
            kind='primary'
            size='small'
            skin='default'
            placement='bottom-end'
            renderPopper={renderPopper}
            onClose={handleClose}
          >
            <Icon icon='filterOutline' />
            {'Filtros' + (countFiltrosAplicados > 0 ? ` (${countFiltrosAplicados})` : '')}
          </PopperButton>
        </HFlow>
      </Cell>

      <Cell size={12}>
        <ListaAtendimentoFilterTags filtros={filter} onChange={onChangeFilter} />
      </Cell>
    </Grid>
  )
}

const createStyles = (theme: Theme) => ({
  hFlow: {
    gridTemplateColumns: '1fr',
  } as CSSProperties,
  cell1: {
    [theme.breakpoints.down('lg')]: {
      width: '39.666%',
    },
  },
  cell2: {
    [theme.breakpoints.down('lg')]: {
      width: '60.333%',
    },
  },
})
